import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { Button, Searchbar, SingleSelect } from '@opengovsg/design-system-react'
import { useToast } from '@opengovsg/design-system-react'
import _ from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CampaignIdentifierType } from '~shared/constants/campaign'

import { AdminCampaignLayout } from '~/layouts/AdminCampaignLayout'
import { DistributionView } from '~/types/distribution'

import DistributionDrawer from '../components/DistributionDrawer'
import { EmptyDistributionsCard } from '../components/EmptyDistributionsCard'

import { PaginationNavigation, TableSkeleton } from '~components/DataTable'
import { DistributionsTable } from '~components/DistributionsTable'
import { DDAction } from '~constants/datadog'
import { useCampaign } from '~hooks/useCampaign'
import { useCreateExportJob } from '~hooks/useCreateExportJob'
import { useDistributionCount } from '~hooks/useDistributionCount'
import {
  DistributionSearchParam,
  useDistributions,
} from '~hooks/useDistributions'
import { useExportJobs } from '~hooks/useExportJobs'
import { useAuth } from '~lib/auth'
import { isApiError } from '~lib/helpers/api'
import { addCustomMonitorClick } from '~lib/helpers/monitoring'

const searchOptions = [
  {
    value: 'identifier',
    label: 'nric'.toUpperCase(),
  },
  {
    value: 'uniqueStringIdentifier',
    label: 'Unique Identifier',
  },
  {
    value: 'address',
    label: 'Address',
  },
  {
    value: 'distributor',
    label: 'Distributor',
  },
  {
    value: 'location',
    label: 'Location',
  },
] as const
// eslint-disable-next-line prettier/prettier
type DistributionSearchOption = typeof searchOptions[number]['value']

const getRequestedExportJobKey = (campaignId: string) =>
  `requested-export-job:${campaignId}`

export const DistributionsPage = (): JSX.Element => {
  const { user } = useAuth()
  const { campaignId } = useParams() as { campaignId: string }
  const { campaign } = useCampaign(campaignId)
  const limit = 10
  const {
    distributions,
    isDistributionsLoading,
    getNextPageOfDistributions,
    isFetchingDistributionsNextPage,
    getPreviousPageOfDistributions,
    isFetchingDistributionsPreviousPage,
    updateFetchDistributionsSearchQuery,
  } = useDistributions(campaignId, limit)
  const { distributionCount } = useDistributionCount(campaignId)

  const { activeJob } = useExportJobs(campaignId)
  const { createExportJob, isCreatingExportJob } =
    useCreateExportJob(campaignId)

  const toast = useToast()

  const handleOnExportButtonClick = async () => {
    try {
      const { data: job } = await createExportJob()
      addCustomMonitorClick(DDAction.EXPORT_DISTRIBUTIONS_ZIP, user?.id ?? '')
      sessionStorage.setItem(getRequestedExportJobKey(campaignId), job.job_id)

      toast({
        title: 'Export started',
        description:
          "Your Distributions report is being prepared. You'll be notified when it's ready.",
        status: 'info',
      })
    } catch (error) {
      if (isApiError(error) && error.httpStatusCode === 422) {
        toast({
          title: 'Export already in progress',
          description:
            'Your Distributions report is already being prepared. Check the notification bell for status updates.',
          status: 'info',
        })
      } else {
        toast({
          title: 'Export failed',
          description:
            'Something went wrong while starting the export. Please try again.',
          status: 'error',
        })
      }
    }
  }

  const getDefaultSearchOption = (
    campaignType?: CampaignIdentifierType,
  ): DistributionSearchOption => {
    switch (campaignType) {
      case 'nric':
        return 'identifier'
      case 'address':
        return 'address'
      case 'unique_string':
        return 'uniqueStringIdentifier'
      // this case ideally should not happen but if it does
      // the default placeholder will still make sense considering
      // how every campaign regardless of campaign type will contain
      // locations which can be searched by
      default:
        return 'location'
    }
  }
  const defaultSearchOption = getDefaultSearchOption(campaign?.identifierType)

  const searchBarRef = useRef<HTMLInputElement>(null)
  const [isUserSearching, setIsUserSearching] = useState(false)
  const [selectedSearchOption, setSelectedSearchOption] =
    useState<DistributionSearchOption>(defaultSearchOption)
  const debouncedSearch = React.useRef(
    _.debounce((searchParam: DistributionSearchParam | undefined) => {
      updateFetchDistributionsSearchQuery(searchParam)
      setIsUserSearching(false)
    }, 300),
  ).current

  const getSearchParam = (
    searchInput: string,
    selectedSearchOption: DistributionSearchOption,
  ): DistributionSearchParam | undefined => {
    if (searchInput === '') {
      return undefined
    }

    switch (selectedSearchOption) {
      case 'identifier':
        return {
          identifier: searchInput,
        }
      case 'address':
        return {
          address: searchInput,
        }
      case 'distributor':
        return {
          distributorIdentifier: searchInput,
        }
      case 'location':
        return {
          locationName: searchInput,
        }
      case 'uniqueStringIdentifier':
        return {
          uniqueStringIdentifier: searchInput,
        }
    }
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUserSearching(true)
    const searchParam = getSearchParam(e.target.value, selectedSearchOption)
    debouncedSearch(searchParam)
  }

  const changeSearchOption = (option: DistributionSearchOption) => {
    if (searchBarRef.current) {
      searchBarRef.current.value = ''
    }

    setSelectedSearchOption(option)
    updateFetchDistributionsSearchQuery(undefined)
  }

  const searchBarPlaceholderText = () => {
    switch (selectedSearchOption) {
      case 'identifier':
        return 'Search by NRIC'
      case 'address':
        return 'Search by Address'
      case 'uniqueStringIdentifier':
        return 'Search by Unique Identifier'
      case 'distributor':
        return "Search by distributor's email or mobile number"
      case 'location':
        return 'Search by location name'
    }
  }

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const data = distributions ?? []

  const totalDistributionCount = distributionCount ?? 0
  // The boolean value indicates whether the campaign contains
  // distribution data. This is determined by checking if the
  // total distribution count is greater than zero. The distribution
  // data is fetched based on the search parameters and is paginated.
  // Therefore, the count might be 0 if there is no distribution data
  // matching the search parameters.
  const hasDistributionData = totalDistributionCount > 0

  // format distribution count
  const distributionCountString = hasDistributionData
    ? `${Intl.NumberFormat('en').format(totalDistributionCount)} ${
        totalDistributionCount > 1 ? 'distributions' : 'distribution'
      }`
    : ''

  // Distribution side-drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedDistribution, setSelectedDistribution] =
    useState<DistributionView>()

  const onDrawerOpen = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])

  const onDrawerClose = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  // helper function to get the valid search identitifers that can be used
  // to search distributions for a particular campaignType
  const getValidSearchOptions = (campaignType?: CampaignIdentifierType) => {
    switch (campaignType) {
      case 'nric':
        return searchOptions.filter(
          (searchOption) =>
            searchOption.value !== 'address' &&
            searchOption.value !== 'uniqueStringIdentifier',
        )

      case 'address':
        return searchOptions.filter(
          (searchOption) =>
            searchOption.value !== 'identifier' &&
            searchOption.value !== 'uniqueStringIdentifier',
        )

      case 'unique_string':
        return searchOptions.filter(
          (searchOption) =>
            searchOption.value !== 'identifier' &&
            searchOption.value !== 'address',
        )
      default:
        return searchOptions
    }
  }
  const validSearchOptions = getValidSearchOptions(campaign?.identifierType)

  return (
    <AdminCampaignLayout
      campaignId={campaignId}
      campaignName={campaign?.name}
      sidebarActiveIndex={4}
      campaignStatus={campaign?.status}
    >
      {campaign && selectedDistribution && (
        <DistributionDrawer
          isOpen={isDrawerOpen}
          onClose={onDrawerClose}
          distribution={selectedDistribution}
          campaign={campaign}
        />
      )}
      <VStack
        alignItems="stretch"
        spacing={8}
        py={12}
        px={6}
        bg="base.canvas.alt"
        minH="$100vh"
        // take up the remaining width of it's parent HStack
        w="100%"
        // override the base margin
        ml="0px !important"
      >
        <VStack alignItems="stretch">
          <Text textStyle="h3" fontWeight="600">
            Distributions
          </Text>
          {hasDistributionData && (
            <Text textStyle="subhead-1" textColor="brand.secondary.500">
              {distributionCountString}
            </Text>
          )}
        </VStack>
        {hasDistributionData && (
          <HStack justifyContent="space-between">
            <HStack flexGrow={0.2}>
              <HStack flexGrow={1} spacing={-1}>
                <Box flex={3}>
                  <SingleSelect
                    name="searchField"
                    value={selectedSearchOption}
                    items={[...validSearchOptions]}
                    isClearable={false}
                    onChange={(val) =>
                      changeSearchOption(val as DistributionSearchOption)
                    }
                  />
                </Box>
                <Box flex={5}>
                  <Searchbar
                    placeholder={searchBarPlaceholderText()}
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    ref={searchBarRef}
                    defaultIsExpanded
                    showClearButton={false}
                    onChange={handleSearch}
                  />
                </Box>
              </HStack>
            </HStack>
            <Button
              size="sm"
              isLoading={isCreatingExportJob}
              isDisabled={data.length === 0 || !!activeJob}
              onClick={handleOnExportButtonClick}
            >
              <Text textStyle="subhead-2">Export All</Text>
            </Button>
          </HStack>
        )}
        {isDistributionsLoading || isUserSearching ? (
          <TableSkeleton />
        ) : hasDistributionData ? (
          <DistributionsTable
            data={data}
            campaign={campaign}
            openDrawer={(d: DistributionView) => {
              onDrawerOpen()
              setSelectedDistribution(d)
            }}
          />
        ) : (
          <EmptyDistributionsCard
            // default to false if campaign data is still not fetched
            isCampaignActive={campaign?.status === 'active'}
            campaignId={campaign?.id}
          />
        )}
        <PaginationNavigation
          key={searchBarRef.current?.value}
          onClickNext={getNextPageOfDistributions}
          isLoadingNextPage={isFetchingDistributionsNextPage ?? false}
          onClickPrevious={getPreviousPageOfDistributions}
          isLoadingPreviousPage={isFetchingDistributionsPreviousPage ?? false}
          limit={limit}
        />
      </VStack>
    </AdminCampaignLayout>
  )
}
